import React, {useRef, useState} from "react";
import {SaveButton} from "../../../../../components/button/SaveButton";
import {CancelButton} from "../../../../../components/button/CancelButton";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslation} from "../../CommonTranslation";
import {getLabelForPropertyType, propertyTypes} from "../PropertyType";
import TextField from "../../../../../components/fields/textfield/TextField";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import Grid from "../../../../../components/dialogs/Grid";
import Snackbar from "../../snackbar/Snackbar";
import {PropertyValidator} from "../PropertyValidator";
import SingleselectComboBox from "../../../../../components/fields/SingleselectComboBox";
import SelectField from "../../../../../components/fields/SelectField";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Constants from "../../../../../common/Constants";
import {PropertyDefinitionDto, PropertyDto} from "../../../../../common/apis/property/PropertyApi";
import {PropertiesTranslationKey} from "../PropertiesTranslationKey";


export interface PropertyCreateApi {
    createProperty: (name: string, type: string, value: string, propertyDefinitionId?: string) => Promise<PropertyDto>;
    getExistingPropertyDefinitions: () => PropertyDefinitionDto[];
    refreshExistingPropertyDefinitions: () => void;
}

export interface PropertyCreateDialogProps {
    open: boolean;
    onCreated: () => void;
    onClosed: () => void;
    existingPropertyNames: Set<string>;
    propertyCreateApi: PropertyCreateApi;
}


export default function PropertyCreateDialog(props: Readonly<PropertyCreateDialogProps>) {
    const validator = useRef<PropertyValidator>(new PropertyValidator());
    const {open, onCreated, onClosed, existingPropertyNames, propertyCreateApi} = props

    const [propertyDefinitionIdentifier, setPropertyDefinitionIdentifier] = useState<string | undefined>(undefined);
    const [name, setName] = useState<string>("");
    const [type, setType] = useState<string>("");
    const [value, setValue] = useState<string>("");

    const [validated, setValidated] = useState<boolean>(false);
    const [nameValidationMsg, setNameValidationMsg] = useState<string | undefined>(undefined);
    const [typeValidationMsg, setTypeValidationMsg] = useState<string | undefined>(undefined);
    const [valueValidationMsg, setValueValidationMsg] = useState<string | undefined>(undefined);

    const propertyDefinitions = propertyCreateApi.getExistingPropertyDefinitions();

    function closeDialog() {
        setName("");
        setNameValidationMsg(undefined);
        setValue("");
        setType("");
        setTypeValidationMsg(undefined);
        onClosed();
    }

    function updateName(newValue: PropertyDefinitionDto | string) {
        if (typeof newValue === 'string') {
            setName(newValue);
            setPropertyDefinitionIdentifier(undefined);
        } else {
            const propertyDefinition = newValue;
            setPropertyDefinitionIdentifier(propertyDefinition.identifier);
            setName(propertyDefinition.name);
            setType(propertyDefinition.type.toLowerCase());
        }
    }

    function validateName(name: string): boolean {
        let isValid = validator.current.validateName(name);
        setNameValidationMsg(isValid ? undefined : t(CommonTranslation.FILL_OUT_THIS_FIELD));
        if (isValid) {
            if (existingPropertyNames.has(name)) {
                setNameValidationMsg(t(ErrorTranslationKey.FAILED_TO_ADD_DUPLICATE_ATTRIBUTE));
                isValid = false;
            } else if (name.trim().toLowerCase() === Constants.STEREOTYPE) {
                setNameValidationMsg(t(ErrorTranslationKey.NOT_ALLOWED_ATTRIBUTE_NAME, name));
                isValid = false;
            }
        }
        return isValid;
    }

    function validateValue(text: string): boolean {
        const isValid = validator.current.validateValue(text);
        setValueValidationMsg(isValid ? undefined : t(CommonTranslation.FILL_OUT_THIS_FIELD));
        return isValid;
    }

    function validateType(text: string): boolean {
        const isValid = validator.current.validateType(text);
        setTypeValidationMsg(isValid ? undefined : t(CommonTranslation.FILL_OUT_THIS_FIELD));
        return isValid;
    }

    function isFormValid() {
        const isNameValid = validateName(name);
        const isTypeValid = validateType(type);
        const isValueValid = validateValue(value);

        return isNameValid && isTypeValid && isValueValid;
    }

    const createProperty = () => {
        setValidated(true)

        if (isFormValid()) {
            propertyCreateApi.createProperty(name.trim(), type, value, propertyDefinitionIdentifier)
                .then(() => {
                    propertyCreateApi.refreshExistingPropertyDefinitions();
                    onCreated();
                    closeDialog();
                    Snackbar.success(t(PropertiesTranslationKey.PROPERTY_CREATED_SUCCESSFULLY));
                })
                .catch((error) => Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error));
        }
    }

    if (!propertyDefinitions) {
        return null;
    }

    return (
        <Dialog open={open}
                onClose={closeDialog}
                fullWidth={true}>

            <DialogTitle id="property-create-dialog"
                         title={t(PropertiesTranslationKey.NEW_PROPERTY)}
                         onDialogClosed={closeDialog}/>

            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <SelectField
                                id="name-field"
                                value={name}
                                label={t(PropertiesTranslationKey.NAME)}
                                freeSolo={true}
                                required={true}
                                errorMessage={nameValidationMsg}
                                options={propertyDefinitions}
                                onChange={(newValue: PropertyDefinitionDto | string) => updateName(newValue)}
                                getOptionLabel={option => option.name} />
                    </Grid>
                    <Grid item xs={12}>
                        <SingleselectComboBox id={"type-field"}
                                              options={propertyTypes.map(propertyType => propertyType.value)}
                                              selectedValue={type}
                                              getRenderLabel={propertyTypeValue => getLabelForPropertyType(propertyTypeValue)!}
                                              label={t(PropertiesTranslationKey.TYPE)}
                                              handleOnChange={(type) => {
                                                  if (validated) {
                                                      validateType(type);
                                                  }
                                                  setType(type)
                                              }}
                                              errorMessage={typeValidationMsg}
                                              required={true}
                                              disabled={propertyDefinitionIdentifier !== undefined}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField id="value-field"
                                   label={t(PropertiesTranslationKey.VALUE)}
                                   value={value}
                                   required={true}
                                   errorMessage={valueValidationMsg}
                                   onChange={(value) => {
                                       if (validated) {
                                           validateValue(value);
                                       }
                                       setValue(value);
                                   }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <SaveButton onClick={createProperty}/>
                <CancelButton onClick={closeDialog} variant={"outlined"}/>
            </DialogActions>
        </Dialog>
    );
}
